import {
   ChangeDetectionStrategy,
   Component,
   Input,
   OnInit,
   OnChanges,
   ViewEncapsulation,
   SimpleChanges,
   ChangeDetectorRef,
   NgZone,
   ElementRef,
   ViewChild,
   EventEmitter,
   Output,
   TemplateRef,
   HostListener,
   Inject,
   PLATFORM_ID,
   OnDestroy,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FilterService } from '../../services/filter.service';
import { CollectionComplianceService } from '../../services/collection-compliance.service';
import { IpoDataService } from '../../services/ipo-data.service';
import { AuthService } from '../../services/auth.service';
import { AccessControlService } from '../../services/access-control.service';
import { Subject, takeUntil } from 'rxjs';
import { PermissionsState } from 'src/app/stores/access-control/access-control.reducers';
import { Store } from '@ngrx/store';
import { getCountryNameByCode } from 'src/app/utils/countries';
import { PORTFOLIO_ASSET_UNKNOWN } from 'src/app/utils/constants';
import { ToastrService } from 'ngx-toastr';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SumPipe } from '../../pipes/sum.pipe';
import { MillionPipe } from '../../pipes/million.pipe';

@Component({
    selector: 'app-dynamic-table',
    templateUrl: './dynamic-table.component.html',
    styleUrls: ['./dynamic-table.component.scss'],
    //encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DynamicTableComponent implements OnInit, OnChanges, OnDestroy {
   private isBrowser: boolean;
   @ViewChild('tableContainer') tableContainer!: ElementRef;
   @Input() showCompliance?: boolean;
   @Input() showComplianceRanking?: boolean;
   @Input() columns: {
      header: string;
      key: any;
      type?: string;
      showPositiveNegative?: boolean;
      commodity_type?: string;
      sortable?: boolean;
      key2?: string;
      type2?: string;
      showPositiveNegative2?: boolean;
      format?: (value: any) => string;
      filterResultFormatFn?: (value: string) => string;
      currencyField?: string;
      hasFilterIcon?: boolean;
   }[] = [];
   @Input() data!: any[];
   @Input() hideFiltersCount: boolean = false;
   @Input() dataCount!: number;
   @Input() isDataLoading!: boolean;
   @Input() activeFiltersCount: number = 0;
   @Input() pageSize: number = 15;
   @Input() page: number = 1;
   @Input() showResetButton: boolean = false;
   @Input() hasAccess: boolean = false;
   @Input() dontShowWatchlist: boolean = false;
   @Input() sortColumn: string = '';
   @Input() sortOrder: string = '';
   @Input() isSortingEnabled: boolean = false;
   @Input() localSort: boolean = false;
   @Input() showSortingArrows: boolean = false;
   @Input() hidePagination: boolean = false;
   @Input() useQueryParams: boolean = true;

   @Input() showCta: boolean = false;
   @Input() type: string = '';
   @Input() isTrending: boolean = false;
   @Input() shouldShowCountAndActiveFilters: boolean = true;

   @Output() scrolled = new EventEmitter<void>();
   @Output() resetAllClicked = new EventEmitter<void>();
   @Output() pageChanged = new EventEmitter<number>();
      @Output() sortChanged = new EventEmitter<{ column: string; sortOrder: string }>();
   @Output() filterIconClicked = new EventEmitter<string>();
   @Input() stockSymbolField: 'ticker' | 'id' = 'id';

   // In dynamic-table.component.ts
   @Input() isIriView: boolean = false;
   // Add this input to control the special layout
   @Input() useSpecialNameLayout: boolean = false;
   @Input() isIriPage: boolean = false;
   @Input() showWatchlist: boolean = true; // Default to true
   @Input() isIpo: any;
   maxSize = 5;
   callTimeMap: { [key: string]: string } = {
      amc: 'After Market Close',
      bmo: 'Before Market Open',
      dmh: 'During Market Hours',
   };

   goToIpoData(row: any) {
      // Navigate with just the symbol in the URL
      this.router.navigate(['/ipo-data', row.company_symbol]);
   }

   getTooltipContent(columnKey: string): string {
      const tooltips: { [key: string]: string } = {
         // example mappings
         // epsEstimate: 'EPS estimate',
         // revenueEstimate: 'Revenue Estimate',
         // revenueActual: 'Revenue Actual',
         // hour: 'Earnings call time',
         // Add other column tooltips here
      };
      return tooltips[columnKey] || '';
   }

   getScoreColor(score: number): string {
      return '';
      switch (score) {
         case 1:
            return 'bg-red';
         case 2:
            return 'bg-orange';
         case 3:
            return 'bg-yellow';
         case 4:
            return 'bg-light-green';
         case 5:
            return 'bg-green';
         default:
            return '';
      }
   }

   // public nameField: string = '';
   // public rankingField: string = 'ranking';
   // public complianceField: string = 'sharia_compliance';
   public complianceFields: string[] = [
      'shariahCompliantStatus',
      'sharia_compliance',
   ];
   public rankingFields: string[] = [
      'ranking',
      'musaffaHalalRating',
      'ranking_v2',
   ];
   public isPaginationDisabled: boolean = false;

   onTableScroll(event: any): void {
      const element = event.target;

      // Calculate the exact position
      const scrollPosition = Math.ceil(
         element.scrollTop + element.clientHeight
      );
      const scrollHeight = element.scrollHeight;

      // Check if we've exactly reached the bottom
      if (scrollHeight - scrollPosition <= 1) {
         this.scrolled.emit();
      }
   }

   getRowType(row: any): string {
      const validTypes = ['stock', 'etf', 'other', 'ipo', 'ecal'];

      if (validTypes.includes(row?.commodity_type)) {
         return row.commodity_type;
      }

      if (validTypes.includes(this.type)) {
         return this.type;
      }
      return 'stock'; // default fallback
   }

   getRatingField() {
      return 'ranking_v2';
   }

   getComplianceField(row: any) {
      return this.getRowType(row) === 'etf'
         ? 'shariahCompliantStatus'
         : 'sharia_compliance';
   }
   // Add this in your component class
   // Add these properties
   // Add these properties at the top of your component class
   tooltipVisible = false;
   tooltipContent = '';
   tooltipPosition = { x: 0, y: 0 };

   // Add these methods
   showHeaderTooltip(key: string, event: MouseEvent): void {
      this.tooltipContent = this.getHeaderTooltip(key);
      this.tooltipPosition = {
         x: event.clientX,
         y: event.clientY + 15, // Position slightly below cursor
      };
      this.tooltipVisible = true;
   }

   hideHeaderTooltip(): void {
      this.tooltipVisible = false;
   }

   getHeaderTooltip(key: string): string {
      const tooltipMap: { [key: string]: string } = {
         epsEstimate: 'EPS estimate',
         revenueEstimate: 'Revenue Estimate',
         revenueActual: 'Revenue Actual',
         hour: 'Earnings Call time',
         // Add more mappings as needed
      };
      return tooltipMap[key] || key;
   }

   getSymbolField(row: any) {
      const type = this.getRowType(row);
      let x = '';
      if (type === 'etf') {
         x = 'symbol';
      } else if (type === 'ecal') {
         x = 'company_symbol'; // or adjust if ecal has a different symbol field
      } else {
         x = (row['id'] && row['id'].length < 36) ? 'id' : 'ticker';
      }
      return x;
   }

   getSymbol(row: any) {
      const symbol =
         row?.symbol ?? row?.ticker ?? row?.id ?? row?.company_symbol;
      //   console.log("row returned:", symbol, "first char:", symbol?.charAt(0));
      return symbol;
   }

   getName(row: any) {
      return (
         row?.companyName ??
         row?.name ??
         row?.provider_description ??
         this.getSymbol(row)
      );
   }

   // renderCount = 0;
   // Range Slider
   greenValue = 40; // Initial value for green slider
   orangeValue = 30; // Initial value for orange slider

   redValue = 30; // Initial value for red slider

   // Minimum width in percentage
   greenMinWidth!: number;
   orangeMinWidth!: number;
   redMinWidth!: number;
   isLoggedIn: boolean = true;
   @ViewChild('unsupportedSymbolModal')
   unsupportedSymbolModal!: TemplateRef<any>;

   @Input() isUsedInWatchlist: boolean = false;
   @Output() deleteClicked = new EventEmitter<any>();

   // Calculate width based on min value
   calculateMinWidth(min: number, max: number): number {
      return (min / max) * 100; // Percentage calculation
   }

   constructor(
      private cdr: ChangeDetectorRef,
      private route: ActivatedRoute,
      private router: Router,
      private collectionCompliance: CollectionComplianceService,
      private filterService: FilterService,
      public accessControlService: AccessControlService,
      private permissionStore: Store<{ permissions: PermissionsState }>,
      private authService: AuthService,
      private toastr: ToastrService,
      private modalService: NgbModal,
      private sumPipe: SumPipe,
      private millionPipe: MillionPipe,
      private ipoDataService: IpoDataService, // Inject the service
      @Inject(PLATFORM_ID) private platformId: Object
   ) {
      this.isBrowser = isPlatformBrowser(this.platformId);
      this.page = this.route.snapshot.queryParams['page'] ?? 1;
   }

   destroy$ = new Subject<void>();
   allowHalalRating: boolean = true;

   ngOnInit(): void {
      if (this.isBrowser) {
         this.updateMaxSize(window.innerWidth);
      } else {
         this.updateMaxSize(1200); // Default for SSR
      }
      this.permissionStore
         .select(
            ({ permissions }) => permissions.permissions.stock_halal_rating
         )
         .pipe(takeUntil(this.destroy$))
         .subscribe((permissions) => {
            this.isLoggedIn = !!this.authService.getUserId();
            this.allowHalalRating = this.isLoggedIn && permissions;
            this.getAccessibleComplianceSymbols();
         });
      this.route.queryParams.subscribe((params: any) => {
         // const filterCount = params
         //    ? Object.keys(params).filter(
         //         (key) =>
         //            key.startsWith('filters[') &&
         //            params[key] !== null &&
         //            params[key] !== undefined &&
         //            params[key] !== '' &&
         //            ((Array.isArray(params[key]) && params[key].length > 0) ||
         //               (!Array.isArray(params[key]) && params[key] !== ''))
         //      ).length
         //    : 0;
         this.filterService.counts$
            .pipe(takeUntil(this.destroy$))
            .subscribe((counts) => {
               this.activeFiltersCount = counts.total;
            });
         this.getAccessibleComplianceSymbols();
         // this.cdr.detectChanges();
      });

      this.greenMinWidth = this.calculateMinWidth(40, 100); // min, max
      this.orangeMinWidth = this.calculateMinWidth(30, 100); // min, max
      this.redMinWidth = this.calculateMinWidth(30, 100);
      this.currentSortColumn = this.sortColumn;
      this.isAscending = this.sortOrder === 'asc';
   }

   @HostListener('window:resize', ['$event'])
   onResize(event: Event): void {
      const width = (event.target as Window).innerWidth;
      this.updateMaxSize(width);
   }

   updateMaxSize(width: number): void {
      this.maxSize = width <= 768 ? 3 : 5;
   }

   resetAllFilters() {
      if (this.type === 'ipo') {
         this.resetAllClicked.emit(); // Notify parent to clear internal filters
      }
      this.filterService.clearAppliedFilters();
      if (this.useQueryParams) {
         const currentParams = { ...this.route.snapshot.queryParams };
         const updatedParams: { [key: string]: undefined } = {};

         Object.keys(currentParams)
            .filter((key) => key.startsWith('filters['))
            .forEach((key) => {
               updatedParams[key] = undefined;
            });
         this.router.navigate([], {
            queryParams: updatedParams,
            queryParamsHandling: 'merge',
            replaceUrl: true,
         });
      }
      this.cdr.markForCheck();
   }

   getPositiveNegativeArrow(value: number): string {
      return value < 0 ? 'icon-caret-down' : 'icon-caret-up';
   }

   ngOnChanges(changes: SimpleChanges): void {
      if (changes['data'] && this.data) {
         setTimeout(() => {
            this.scrollToPosition();
         });
         this.getAccessibleComplianceSymbols();

         // const firstRow = this.data[0];

         // if (firstRow?.companyName) {
         //    this.nameField = 'companyName';
         // } else if (firstRow?.name) {
         //    this.nameField = 'name';
         // } else {
         //    this.nameField = 'ticker';
         // }

         // if (this.type === 'etf') {
         //    this.rankingField = 'musaffaHalalRating';
         //    this.complianceField = 'shariahCompliantStatus';
         // }
      }

      // const firstRow = this.data[0];
      // if (firstRow?.companyName) {
      //    this.nameField = 'companyName';
      // } else if (firstRow?.name) {
      //    this.nameField = 'name';
      // } else {
      //    this.nameField = 'ticker';
      // }
      // this.renderCount++;
      // console.log(`Render count: ${this.renderCount}`);
      // this.cdr.markForCheck();
      // console.log(this.data)
   }

   trackByFn(index: number, item: any): any {
      return index;
   }

   onDelete(stock: any) {
      this.deleteClicked.emit(stock);
   }

   getOverviewRoute(row: any):any[] | null {
      if (!row?.id) return null;
      if (this.isRowLocked(row)) return null;

      const rowType = this.getRowType(row);
      const symbol = row[this.getSymbolField(row)];

      if (rowType === 'etf') {
         return ['/etf', symbol];
      } else if (rowType === 'stock' || rowType === 'ecal') {
         return ['/stock', symbol];
      }
      return null;
   }

   redirectToOverview(event: Event, row: any) {
      //console.log("called")
      event.stopPropagation();
      // If we have a route, the routerLink handles proper navigation (including Ctrl+Click)
      // If we don't have a valid route (e.g. no ID), we show the modal
      if (!this.getOverviewRoute(row)) {
         this.commodityNotAvailable();
      }
   }

   openSignupPopup() {
      this.authService.openAuthPopup(null, null, 'signUp', 'waitlist');
   }

   isRowLocked(row: any): boolean {
      if (!row) return false;
      const symbol = this.getSymbol(row);
      if (this.accessibleComplianceSymbols.includes(symbol)) {
         return false;
      }
      return row.locked;
   }

   handleLockedClick(event?: MouseEvent) {
      if (event) {
         event.stopPropagation();
      }
      if (!this.authService.isLoggedIn()) {
         this.openSignupPopup();
         return;
      }
      this.router.navigate(['/pricing']);
   }

   commodityNotAvailable() {
      this.modalService.dismissAll();
      this.modalService.open(this.unsupportedSymbolModal, {
         centered: true,
         keyboard: true,
         size: 'sm',
      });
   }

   getCellClass(
      columnField: string,
      value: any,
      showPositiveNegative?: boolean
   ): string {
      const complianceClasses: { [key: string]: string } = {
         COMPLIANT: 'compliance-chip halal-chip',
         NON_COMPLIANT: 'compliance-chip not-halal-chip',
         QUESTIONABLE: 'compliance-chip doubtful-chip',
         ranking_v2: 'rating_grade', // Ensure this class is used
         ranking: 'rating_grade',
         default: 'compliance-chip not-covered-chip',
      };

      const analystsRatingClasses: { [key: string]: string } = {
         'Strong sell': 'analysts-rating strong-sell-class',
         Sell: 'analysts-rating sell-class',
         SELL: 'analysts-rating sell-class',
         Hold: 'analysts-rating hold-class',
         Buy: 'analysts-rating buy-class',
         BUY: 'analysts-rating buy-class',
         'Strong buy': 'analysts-rating strong-buy-class',
      };

      // ✅ Explicitly check for ranking_v2 first
      if (columnField === 'ranking_v2') {
         return complianceClasses['ranking_v2']; // Will return 'ranking-text'
      }

      if (
         columnField === 'status' ||
         this.rankingFields.includes(columnField) ||
         this.complianceFields.includes(columnField) ||
         columnField === 'shariahStates'
      ) {
         return complianceClasses[value] || complianceClasses['default'];
      }

      if (
         columnField === 'analyst_recommendation_weighted_avg' ||
         columnField === 'type'
      ) {
         return analystsRatingClasses[value] || '';
      }

      if (showPositiveNegative) {
         return value < 0 ? 'down_price' : 'up_price';
      }

      this.cdr.markForCheck();
      return '';
   }

   getFormattedValues(key: string, value: any, type?: string) {
      const complianceDisplayValues: { [key: string]: string } = {
         COMPLIANT: 'Halal',
         NON_COMPLIANT: 'Not Halal',
         QUESTIONABLE: 'Doubtful',
         default: 'Not Covered',
      };
      const analystsRatingDisplayValues: { [key: string]: string } = {
         'strong sell': 'Strong Sell',
         sell: 'Sold',
         hold: 'Hold',
         buy: 'Buy',
         'strong buy': 'Strong Buy',
      };

      // Handle numeric values - round to 2 decimal places
      if (value === null || value === undefined) return value;

      const numValue = typeof value === 'string' ? Number(value) : value;

      if (type === 'number' || type === 'percent') {
         return Number.isInteger(numValue) ? numValue : numValue.toFixed(2);
      }

      switch (key) {
         case 'aum':
            return (numValue / 1e9).toFixed(2);
         case 'lastPrice':
            return Number(numValue.toFixed(2));
         case 'priceChangePercent':
            return Number(numValue.toFixed(2));
         case 'status':
            return complianceDisplayValues[value] || value;
         case 'musaffaHalalRating':
            return complianceDisplayValues[value] || value;
         case 'shariahCompliantStatus':
            return complianceDisplayValues[value] || value;
         case 'shariahStates':
            return complianceDisplayValues[value] || value;
         case 'recommededStatus':
            return analystsRatingDisplayValues[value] || value;
         default:
            return value;
      }
   }

   formatPriceRange(range: string, currency: string = ''): string {
      if (!range) return '-';

      // Map exchange code to currency if needed
      const currencyMap: { [key: string]: string } = {
         IN: 'INR',
         US: 'USD'
         // Add more mappings if needed
      };

      const resolvedCurrency = currencyMap[currency] || currency;

      try {
         const parts = range.split('-').map((part) => part.trim());

         if (parts.length === 1) {
            const num = parseFloat(parts[0]);
            return isNaN(num) ? range : this.sumPipe.transform(num, resolvedCurrency);
         }

         if (parts.length === 2) {
            const firstNum = parseFloat(parts[0]);
            const secondNum = parseFloat(parts[1]);

            if (!isNaN(firstNum) && !isNaN(secondNum)) {
               return `${this.sumPipe.transform(firstNum, resolvedCurrency)} - ${this.sumPipe.transform(secondNum, resolvedCurrency)}`;
            }
         }

         return range;
      } catch (e) {
         return range;
      }
   }
   formatDecimal(value: any): string {
      if (value === -1 || value === null || value === undefined) return '-';

      const num = parseFloat(value);
      return isNaN(num) ? '-' : num.toFixed(2);
   }


   formatMillionOffer(value: any, currency: string = ''): string {
      if (value === null || value === undefined || value === '') return '-';

      const currencyMap: { [key: string]: string } = {
         IN: 'INR',
         US: 'USD'
      };

      const resolvedCurrency = currencyMap[currency] || currency;

      if (value === 0) {
         return this.sumPipe.transform(0, ''); // No currency for zero
      }

      try {
         const millionFormatted = this.millionPipe.transform(value, false);

         if (millionFormatted === '-') return millionFormatted;

         if (!/[KMBT]$/.test(millionFormatted)) {
            const numeric = parseFloat(millionFormatted);
            return this.sumPipe.transform(numeric, resolvedCurrency);
         }

         const match = millionFormatted.match(/^([0-9.,+-]+)([KMBT])$/);
         if (!match) return millionFormatted;

         const [_, numericStr, suffix] = match;
         const numeric = parseFloat(numericStr.replace(/,/g, ''));

         const currencyOnly = this.sumPipe
            .transform(0, resolvedCurrency)
            .replace(/0.00?$/, '')
            .replace(/0$/, '');

         return `${currencyOnly}${numeric.toFixed(2)}${suffix}`;
      } catch {
         return value.toString();
      }
   }



   isSortable(columnKey: string): boolean {
      if (this.rankingFields.includes(columnKey)) {
         return this.allowHalalRating;
      }

      const column = this.columns.find((column) => column.key === columnKey);
      return this.isSortingEnabled && (column?.sortable ?? true);
   }

   onPageChange(page: number): void {
      if (!this.isPaginationDisabled) {
         this.isPaginationDisabled = true;
         if (this.useQueryParams) {
            this.router.navigate([], {
               queryParams: { page: page },
               queryParamsHandling: 'merge',
               replaceUrl: true,
            });
         } else {
            this.pageChanged.emit(page);
         }

         setTimeout(() => {
            this.isPaginationDisabled = false;
            this.cdr.markForCheck();
         }, 300); // Delay to handle quick toggling
      }
   }

   private scrollToPosition() {
      if (this.data.length > 16 && this.tableContainer) {
         const container = this.tableContainer.nativeElement;
         const rowHeight = container.querySelector('tr')?.offsetHeight || 0;
         const scrollPosition = (this.data.length - 15) * rowHeight;
         container.scrollTop = scrollPosition;
      }
   }

   // getColumnClass(columnField: string): string {
   //    switch (columnField) {
   //       case 'compliance':
   //          return 'compliance-chip';
   //       case 'rating':
   //          return 'rating-column';
   //       case 'analysts_ratings':
   //          return 'analysts-rating';
   //       default:
   //          return '';
   //    }
   // }

   // TABLE SORTING
   currentSortColumn: string = '';
   isAscending: boolean = true;
   @Output() sortEvent = new EventEmitter<{
      column: string;
      sortOrder: string;
   }>();

   sortTable(column: string) {
      if (this.isSortable(column)) {
         if (this.currentSortColumn === column) {
            this.isAscending = !this.isAscending;
         } else {
            this.currentSortColumn = column;
            this.isAscending = true;
         }
         if (this.localSort) {
            const sortFactor = this.isAscending ? 1 : -1;
            this.data = this.data || [];
            this.data.sort((a: any, b: any) => {
               const aVal = this.getSortValue(a, column);
               const bVal = this.getSortValue(b, column);
               if (aVal == null && bVal == null) return 0;
               if (aVal == null) return 1 * sortFactor;
               if (bVal == null) return -1 * sortFactor;
               if (typeof aVal === 'string' && typeof bVal === 'string') {
                  return aVal.localeCompare(bVal) * sortFactor;
               }
               return (Number(aVal) > Number(bVal) ? 1 : -1) * sortFactor;
            });
            this.cdr.markForCheck();
            return;
         }
         if (this.useQueryParams) {
            this.router.navigate([], {
               queryParams: {
                  sortBy: column,
                  sortOrder: this.isAscending ? 'asc' : 'desc',
               },
               queryParamsHandling: 'merge',
               replaceUrl: true,
            });
         } else {
            this.sortChanged.emit({
               column,
               sortOrder: this.isAscending ? 'asc' : 'desc',
            });
         }
      }
      // this.sortEvent.emit({ column, sortOrder: this.isAscending ? 'asc' : 'desc' });
   }

   private getSortValue(row: any, column: string): any {
      if (!row) return null;
      let value = row[column];
      if (value === undefined && typeof column === 'string') {
         const camel = column.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
         value = row[camel];
      }
      if (value === undefined || value === null) return value;
      if (typeof value === 'string') {
         const cleaned = value.replace(/[%,$]/g, '').replace(/,/g, '').trim();
         if (cleaned !== '' && !isNaN(Number(cleaned))) return Number(cleaned);
         return value.toUpperCase();
      }
      return value;
   }

   isSorted(column: string): boolean {
      return this.currentSortColumn === column;
   }

   // Table Filter Select Dropdown
   openFilters: { [key: string]: boolean } = {};

   toggleFilterDropdown(columnKey: string): void {
      this.openFilters[columnKey] = !this.openFilters[columnKey];
   }

   onFilterIconClick(columnKey: string, event: Event): void {
      event.stopPropagation();
      this.filterIconClicked.emit(columnKey);
   }

   isFilterOpen(columnKey: string): boolean {
      return !!this.openFilters[columnKey];
   }

   accessibleComplianceSymbols: string[] = [];

   async getAccessibleComplianceSymbols() {
      const etfSymbols: string[] = [];
      const stockSymbols: string[] = [];
      this.data.forEach((row) => {
         if (this.getRowType(row) === 'etf') {
            etfSymbols.push(row['symbol']);
         } else {
            // stockSymbols.push(row['id']);
            const value = row?.[this.stockSymbolField];

            if (value != null) {
               stockSymbols.push(value);
            }
         }
      });
      this.accessibleComplianceSymbols = [
         ...(await this.collectionCompliance.getAccessibleComplianceSymbols(
            etfSymbols,
            'etfs'
         )),
         ...(await this.collectionCompliance.getAccessibleComplianceSymbols(
            stockSymbols,
            'stocks'
         )),
      ];

      this.cdr.markForCheck();
   }

   getCountryName(countryCode: string): string {
      if (countryCode === 'NOT_COVERED') {
         return PORTFOLIO_ASSET_UNKNOWN;
      }
      return getCountryNameByCode(countryCode) ?? countryCode;
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }
}
